import { useState, useEffect } from "react";
import axios from "axios";
import swal from "sweetalert";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";

// Material Dashboard 3 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";

import { logoutUser } from "actions/AuthAction";
import { useNavigate } from "react-router-dom";
import { API_ESA_OFFICE } from "utils";

function StatistikTamu() {
  const [dinasId, setDinasId] = useState(null);
  const [statistik, setStatistik] = useState(null);
  const [statistikDetail, setStatistikDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTahun, setSelectedTahun] = useState(new Date().getFullYear());
  const navigate = useNavigate();

  // Generate tahun list (current year down to 2020)
  const tahunList = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i >= 2020; i--) {
    tahunList.push(i);
  }

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setDinasId(user.dinas_id);
      fetchStatistik(user.dinas_id, selectedTahun);
    } else {
      logoutUser(navigate);
    }
  }, []);

  useEffect(() => {
    if (dinasId) {
      fetchStatistik(dinasId, selectedTahun);
      fetchStatistikDetail(dinasId, selectedTahun);
    }
  }, [selectedTahun, dinasId]);

  const fetchStatistik = async (idDinas, tahun) => {
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}buku-tamu/statistik/${idDinas}?tahun=${tahun}`
      );
      setStatistik(response.data);
    } catch (error) {
      console.error("Error fetching statistik:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data statistik",
        icon: "error",
      });
    }
  };

  const fetchStatistikDetail = async (idDinas, tahun) => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${API_ESA_OFFICE}buku-tamu/statistik-detail/${idDinas}?tahun=${tahun}`
      );
      setStatistikDetail(response.data);
    } catch (error) {
      console.error("Error fetching statistik detail:", error);
      swal({
        title: "Error",
        text: "Gagal mengambil data statistik detail",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading && !statistik) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            minHeight="400px"
          >
            <CircularProgress />
            <MDTypography variant="body2" color="text" ml={2}>
              Memuat data...
            </MDTypography>
          </MDBox>
        </MDBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <MDTypography variant="h6" color="white">
                    Statistik Tamu
                  </MDTypography>
                  <Autocomplete
                    options={tahunList}
                    value={selectedTahun}
                    onChange={(event, newValue) => {
                      if (newValue) setSelectedTahun(newValue);
                    }}
                    renderInput={(params) => (
                      <MDInput
                        {...params}
                        label="Pilih Tahun"
                        variant="outlined"
                        sx={{
                          backgroundColor: "white",
                          minWidth: 150,
                        }}
                      />
                    )}
                    getOptionLabel={(option) => option.toString()}
                  />
                </MDBox>
              </MDBox>
              <MDBox pt={4} pb={3} px={3}>
                {loading && !statistikDetail ? (
                  <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="200px"
                  >
                    <CircularProgress />
                    <MDTypography variant="body2" color="text" ml={2}>
                      Memuat data...
                    </MDTypography>
                  </MDBox>
                ) : (
                  <>
                    {/* Statistik Overview */}
                    {statistik && (
                      <Grid container spacing={3} mb={4}>
                        <Grid item xs={12} md={4}>
                          <Card>
                            <CardContent>
                              <MDTypography variant="h6" color="text.secondary">
                                Total Tamu
                              </MDTypography>
                              <MDTypography
                                variant="h3"
                                color="info"
                                fontWeight="bold"
                              >
                                {statistik.total_tamu || 0}
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                              >
                                Tahun {statistik.tahun}
                              </MDTypography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Card>
                            <CardContent>
                              <MDTypography variant="h6" color="text.secondary">
                                Total Hari
                              </MDTypography>
                              <MDTypography
                                variant="h3"
                                color="success"
                                fontWeight="bold"
                              >
                                {statistik.total_hari || 0}
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                              >
                                Hari dengan kunjungan
                              </MDTypography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Card>
                            <CardContent>
                              <MDTypography variant="h6" color="text.secondary">
                                Total Instansi
                              </MDTypography>
                              <MDTypography
                                variant="h3"
                                color="warning"
                                fontWeight="bold"
                              >
                                {statistik.total_instansi || 0}
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                              >
                                Instansi berbeda
                              </MDTypography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    )}

                    {/* Grafik Tamu Per Bulan */}
                    {statistikDetail && statistikDetail.per_bulan && (
                      <Grid container spacing={3} mb={4}>
                        <Grid item xs={12}>
                          <ReportsLineChart
                            color="info"
                            title="Grafik Tamu Per Bulan"
                            description={`Tahun ${selectedTahun}`}
                            date="Data berdasarkan bulan"
                            chart={{
                              labels: statistikDetail.per_bulan.map(
                                (b) => b.nama_bulan
                              ),
                              datasets: {
                                label: "Jumlah Tamu",
                                data: statistikDetail.per_bulan.map(
                                  (b) => b.jumlah_tamu
                                ),
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}

                    {/* Statistik Per Bidang dan Per Keperluan */}
                    {statistikDetail && (
                      <Grid container spacing={3} mb={4}>
                        <Grid item xs={12} md={6}>
                          <ReportsBarChart
                            color="success"
                            title="Statistik Tamu Per Bidang"
                            description={`Top 10 Bidang - Tahun ${selectedTahun}`}
                            date="Data berdasarkan bidang"
                            chart={{
                              labels:
                                statistikDetail.per_bidang.length > 0
                                  ? statistikDetail.per_bidang.map(
                                      (b) => b.nama_bidang
                                    )
                                  : ["Tidak Ada Data"],
                              datasets: {
                                label: "Jumlah Tamu",
                                data:
                                  statistikDetail.per_bidang.length > 0
                                    ? statistikDetail.per_bidang.map(
                                        (b) => b.jumlah_tamu
                                      )
                                    : [0],
                              },
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <ReportsBarChart
                            color="warning"
                            title="Statistik Tamu Per Keperluan"
                            description={`Top 10 Keperluan - Tahun ${selectedTahun}`}
                            date="Data berdasarkan keperluan"
                            chart={{
                              labels:
                                statistikDetail.per_keperluan.length > 0
                                  ? statistikDetail.per_keperluan.map(
                                      (k) => k.keperluan
                                    )
                                  : ["Tidak Ada Data"],
                              datasets: {
                                label: "Jumlah Tamu",
                                data:
                                  statistikDetail.per_keperluan.length > 0
                                    ? statistikDetail.per_keperluan.map(
                                        (k) => k.jumlah_tamu
                                      )
                                    : [0],
                              },
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}

                    {/* Top 5 ASN */}
                    {statistikDetail && statistikDetail.top_asn && (
                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Card>
                            <MDBox p={3}>
                              <MDTypography variant="h6" mb={2}>
                                Top 5 ASN yang Menerima Tamu Terbanyak
                              </MDTypography>
                              <MDTypography
                                variant="caption"
                                color="text.secondary"
                                mb={3}
                              >
                                Bulan{" "}
                                {new Date().toLocaleDateString("id-ID", {
                                  month: "long",
                                  year: "numeric",
                                })}
                              </MDTypography>
                              {statistikDetail.top_asn.length > 0 ? (
                                <Grid container spacing={2}>
                                  {statistikDetail.top_asn.map((asn, index) => (
                                    <Grid
                                      item
                                      xs={12}
                                      sm={6}
                                      md={4}
                                      key={index}
                                    >
                                      <Card
                                        sx={{
                                          height: "100%",
                                          boxShadow: 2,
                                          transition: "all 0.3s ease",
                                          "&:hover": {
                                            boxShadow: 4,
                                            transform: "translateY(-4px)",
                                          },
                                        }}
                                      >
                                        <CardContent>
                                          <MDBox
                                            display="flex"
                                            alignItems="center"
                                            justifyContent="space-between"
                                            mb={1}
                                          >
                                            <MDBox
                                              sx={{
                                                width: 32,
                                                height: 32,
                                                borderRadius: "50%",
                                                backgroundColor:
                                                  index === 0
                                                    ? "success.main"
                                                    : index === 1
                                                    ? "info.main"
                                                    : index === 2
                                                    ? "warning.main"
                                                    : "grey.300",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "center",
                                                color: "white",
                                                fontWeight: "bold",
                                              }}
                                            >
                                              <MDTypography
                                                variant="button"
                                                color="white"
                                                fontWeight="bold"
                                              >
                                                {index + 1}
                                              </MDTypography>
                                            </MDBox>
                                            <MDTypography
                                              variant="h4"
                                              color={
                                                index === 0
                                                  ? "success"
                                                  : index === 1
                                                  ? "info"
                                                  : index === 2
                                                  ? "warning"
                                                  : "text"
                                              }
                                              fontWeight="bold"
                                            >
                                              {asn.jumlah_tamu}
                                            </MDTypography>
                                          </MDBox>
                                          <MDTypography
                                            variant="h6"
                                            color="text"
                                            fontWeight="medium"
                                            sx={{
                                              wordWrap: "break-word",
                                              whiteSpace: "normal",
                                            }}
                                          >
                                            {asn.nama}
                                          </MDTypography>
                                          <MDTypography
                                            variant="caption"
                                            color="text.secondary"
                                            mt={0.5}
                                          >
                                            Tamu
                                          </MDTypography>
                                        </CardContent>
                                      </Card>
                                    </Grid>
                                  ))}
                                </Grid>
                              ) : (
                                <MDBox
                                  display="flex"
                                  justifyContent="center"
                                  alignItems="center"
                                  py={4}
                                >
                                  <MDTypography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Belum ada data untuk bulan ini
                                  </MDTypography>
                                </MDBox>
                              )}
                            </MDBox>
                          </Card>
                        </Grid>
                      </Grid>
                    )}
                  </>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default StatistikTamu;
